import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { lockScroll } from "../lib/scroll";
import { EASE } from "./Reveal";

export default function AuthModal({ open, onClose, initialTab = "login" }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setError("");
    }
    lockScroll(open);
    return () => lockScroll(false);
  }, [open, initialTab]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (tab === "register") {
        await register(form.name, form.email, form.password);
        toast.success("Welcome to VIVORÉ — your 10% off code is on its way to your inbox.");
      } else {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      }
      setForm({ name: "", email: "", password: "" });
      onClose();
    } catch (err) {
      setError(err.message || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div data-testid="auth-modal" className="fixed inset-0 z-[85]">
          <motion.div
            className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="absolute inset-x-4 top-1/2 -translate-y-1/2 sm:inset-x-0 sm:mx-auto sm:max-w-[420px] bg-[#F8F5F2] rounded-[26px] p-8 md:p-10 shadow-2xl"
          >
            <button
              data-testid="auth-close"
              onClick={onClose}
              className="absolute top-5 right-5 p-2 text-ink/50 hover:text-ink transition-colors"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <p className="eyebrow">Vivoré</p>
            <h3 className="font-display text-3xl mt-2">
              {tab === "login" ? "Welcome back" : "Join the VIVORÉ list"}
            </h3>

            <div className="flex gap-6 mt-6 border-b border-ink/10">
              {[
                { id: "login", label: "Sign in" },
                { id: "register", label: "Create account" },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`auth-tab-${t.id}`}
                  onClick={() => {
                    setTab(t.id);
                    setError("");
                  }}
                  className={`pb-3 font-sans text-[12px] uppercase tracking-widest2 transition-colors duration-300 border-b -mb-px ${
                    tab === t.id ? "text-ink border-ink" : "text-stone border-transparent hover:text-ink"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-6 space-y-5">
              {tab === "register" && (
                <label className="block">
                  <span className="eyebrow !text-[10px] mb-1.5 block">Full name</span>
                  <input
                    data-testid="auth-input-name"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    className="w-full bg-transparent border-b border-ink/20 focus:border-lav outline-none py-2.5 font-light text-sm transition-colors duration-500"
                  />
                </label>
              )}
              <label className="block">
                <span className="eyebrow !text-[10px] mb-1.5 block">Email</span>
                <input
                  data-testid="auth-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  className="w-full bg-transparent border-b border-ink/20 focus:border-lav outline-none py-2.5 font-light text-sm transition-colors duration-500"
                />
              </label>
              <label className="block">
                <span className="eyebrow !text-[10px] mb-1.5 block">Password</span>
                <input
                  data-testid="auth-input-password"
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={set("password")}
                  className="w-full bg-transparent border-b border-ink/20 focus:border-lav outline-none py-2.5 font-light text-sm transition-colors duration-500"
                />
                {tab === "register" && (
                  <span className="block mt-1.5 font-sans text-[11px] text-stone">
                    At least 8 characters. You&apos;ll receive 10% off your first order by email.
                  </span>
                )}
              </label>

              {error && (
                <p data-testid="auth-error" className="font-sans text-[12px] text-[#A3413C] leading-relaxed">
                  {error}
                </p>
              )}

              <button
                data-testid="auth-submit"
                type="submit"
                disabled={busy}
                className="btn-lux w-full bg-ink text-ivory text-xs uppercase tracking-widest2 py-4 rounded-full disabled:opacity-60"
              >
                {busy ? "One moment…" : tab === "login" ? "Sign in" : "Create account & get 10% off"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
